import { AfterViewChecked, Component, ElementRef, computed, inject, input } from '@angular/core';
import { botAvatarDataUri } from '@afs/chat-core';
import type { ChatMessage, Participant } from '@afs/chat-core';

/**
 * How close to the bottom still counts as being at the bottom. Not zero: a box
 * scrolled all the way down almost never reports
 * scrollTop + clientHeight === scrollHeight. Sub-pixel scroll positions and the
 * last message's bottom margin leave a few pixels over, so an exact test would
 * decide the reader had scrolled up when they had not, and the thread would
 * stop following new messages.
 */
const BOTTOM_THRESHOLD_PX = 48;

interface Line {
  id: number;
  body: string;
  kind: ChatMessage['kind'];
  sender: string;
  mine: boolean;
  avatar: string | null;
  time: string;
}

/**
 * The message list. Pure input/output: it does not know SurveyChatService
 * exists, which is what makes each rendering rule one short test that passes an
 * array in rather than faking Supabase.
 *
 * Every class is prefixed `afs-chat-` and lives in this component's own inline
 * styles: NobleUI already ships a `.chat-thread` / `.chat-msg` set for its demo
 * chat page, and the two must not meet.
 */
@Component({
  selector: 'app-chat-thread',
  standalone: true,
  styles: [`
    .afs-chat-thread { flex: 1; min-height: 0; padding: .6rem; overflow-y: auto; }
    .afs-chat-thread__empty,
    .afs-chat-thread__typing { margin: 0; font-size: .78rem; opacity: .7; }
    .afs-chat-msg { display: flex; gap: .45rem; margin-bottom: .45rem; }
    .afs-chat-msg--mine { justify-content: flex-end; }
    .afs-chat-msg--mine .afs-chat-msg__body { background: rgba(218,39,42,.14); }
    .afs-chat-msg--system { justify-content: center; }
    .afs-chat-msg--system .afs-chat-msg__body {
      background: transparent; font-size: .73rem; opacity: .7;
    }
    .afs-chat-msg__avatar { width: 28px; height: 28px; border-radius: 50%; flex: none; }
    .afs-chat-msg__body {
      max-width: 78%; padding: .35rem .55rem; border-radius: 4px;
      background: rgba(127,127,127,.14);
    }
    .afs-chat-msg__sender { display: block; font-size: .68rem; font-weight: 600; opacity: .8; }
    .afs-chat-msg__text {
      margin: 0; font-size: .83rem; white-space: pre-wrap; word-break: break-word;
    }
    .afs-chat-msg__time { font-size: .65rem; opacity: .6; }
  `],
  template: `
    <div class="afs-chat-thread" (scroll)="onScroll()">
      @if (lines().length === 0) {
        <p class="afs-chat-thread__empty">No messages yet.</p>
      }

      @for (line of lines(); track line.id) {
        <div class="afs-chat-msg"
             [class.afs-chat-msg--mine]="line.mine"
             [class.afs-chat-msg--system]="line.kind === 'system'">
          @if (line.avatar !== null) {
            <img class="afs-chat-msg__avatar" [src]="line.avatar" alt="" aria-hidden="true" />
          }
          <div class="afs-chat-msg__body">
            @if (line.kind !== 'system' && !line.mine) {
              <span class="afs-chat-msg__sender">{{ line.sender }}</span>
            }
            <p class="afs-chat-msg__text">{{ line.body }}</p>
            <span class="afs-chat-msg__time">{{ line.time }}</span>
          </div>
        </div>
      }

      @if (typingName() !== null) {
        <p class="afs-chat-thread__typing" aria-live="polite">{{ typingName() }} is typing…</p>
      }
    </div>
  `,
})
export class ChatThreadComponent implements AfterViewChecked {
  private readonly hostRef: ElementRef<HTMLElement> = inject(ElementRef);

  /** Line count at the last scroll decision, so only growth moves the reader. */
  private renderedLines = 0;

  /** Starts true: an opened thread shows its newest message, not its oldest. */
  private pinnedToBottom = true;

  readonly messages = input.required<readonly ChatMessage[]>();
  readonly participants = input.required<readonly Participant[]>();
  readonly selfUserId = input.required<string>();
  readonly typingName = input<string | null>(null);

  readonly lines = computed<Line[]>(() => {
    // The participant row snapshots the display name at join time, so a later
    // rename does not rewrite history (spec section 9).
    const names = new Map(this.participants().map((p) => [p.userId, p.displayName]));

    return this.messages().map((message) => ({
      id: message.id,
      body: message.body,
      kind: message.kind,
      // A participant can leave. Showing a raw UUID — or "undefined" — to a
      // helper on a roof helps nobody, so an unknown sender becomes a neutral
      // word. senderId is null only for system and bot lines, and neither
      // renders a sender, so there is no separate null branch to test.
      sender: names.get(message.senderId ?? '') ?? 'Someone',
      mine: message.senderId === this.selfUserId(),
      // Seeded by conversation, so one assistant keeps one face for the whole
      // thread without any avatar being stored or fetched anywhere.
      avatar: message.kind === 'bot'
        ? botAvatarDataUri(message.conversationId, 28)
        : null,
      // No timezone is named here on purpose: the tablet's own locale decides.
      time: new Date(message.createdAt)
        .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    }));
  });

  /**
   * Follow the newest message once the rows for it are in the DOM. Only a
   * change in the number of lines is acted on: this hook runs after every
   * change detection pass, and scrolling on each one would fight the reader
   * for the scrollbar.
   */
  ngAfterViewChecked(): void {
    const count = this.lines().length;
    if (count === this.renderedLines) {
      return;
    }
    this.renderedLines = count;

    // Someone reading back through history keeps their place. They rejoin the
    // live end of the thread by scrolling back down, which re-pins them.
    if (!this.pinnedToBottom) {
      return;
    }

    const box = this.box();
    if (box !== null) {
      box.scrollTop = box.scrollHeight;
    }
  }

  onScroll(): void {
    const box = this.box();
    if (box !== null) {
      this.pinnedToBottom = ChatThreadComponent.atBottom(
        box.scrollTop,
        box.scrollHeight,
        box.clientHeight,
      );
    }
  }

  /**
   * The whole decision, as arithmetic, so it can be tested without a layout
   * engine: a test runner on jsdom reports every box as zero-sized.
   */
  static atBottom(scrollTop: number, scrollHeight: number, clientHeight: number): boolean {
    return scrollHeight - scrollTop - clientHeight <= BOTTOM_THRESHOLD_PX;
  }

  private box(): HTMLElement | null {
    return this.hostRef.nativeElement.querySelector<HTMLElement>('.afs-chat-thread');
  }
}
